import { useRef, useEffect } from 'react';
import { SceneManager } from '../lib/SceneManager';
import type { SessionData } from '../types';
import type { ImageryResult } from '../lib/satellite';

interface Canvas3DProps {
  data: SessionData | null;
  moduleId: string;
  removeMode: boolean;
  shadeVisible: boolean;
  imagery: ImageryResult | null;
  onPanelRemoved: (panelId: string) => void;
  /** Filled with a capture function once the scene exists, so the app can grab a thumbnail on save. */
  snapshotRef?: React.MutableRefObject<(() => string | null) | null>;
}

export function Canvas3D({ data, moduleId, removeMode, shadeVisible, imagery, onPanelRemoved, snapshotRef }: Canvas3DProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneManagerRef = useRef<SceneManager | null>(null);

  // The scene is initialized once, but the removal handler closes over
  // session state — route through a ref so clicks always hit the latest one.
  const onPanelRemovedRef = useRef(onPanelRemoved);
  useEffect(() => {
    onPanelRemovedRef.current = onPanelRemoved;
  }, [onPanelRemoved]);

  // Initialize scene once
  useEffect(() => {
    if (!containerRef.current) return;
    const sm = new SceneManager(containerRef.current);
    sm.setCallbacks({
      onPanelClick: (panelId) => onPanelRemovedRef.current(panelId),
      onPanelHover: (info, x, y) => {
        const tooltip = document.getElementById('info-tooltip');
        if (tooltip) {
          tooltip.textContent = info;
          tooltip.style.display = 'block';
          tooltip.style.left = `${x + 15}px`;
          tooltip.style.top = `${y + 15}px`;
        }
      },
      onHoverEnd: () => {
        const tooltip = document.getElementById('info-tooltip');
        if (tooltip) tooltip.style.display = 'none';
      },
    });
    sceneManagerRef.current = sm;
    if (snapshotRef) snapshotRef.current = () => sm.captureSnapshot();

    return () => {
      sm.dispose();
      sceneManagerRef.current = null;
      if (snapshotRef) snapshotRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load satellite imagery when available
  useEffect(() => {
    const sm = sceneManagerRef.current;
    if (!sm) return;
    if (imagery) {
      sm.loadSatelliteImagery(imagery);
    } else {
      sm.clearSatelliteImagery();
    }
  }, [imagery]);

  // Render design when data changes
  useEffect(() => {
    if (sceneManagerRef.current && data) {
      sceneManagerRef.current.renderDesign(data, moduleId);
    }
  }, [data, moduleId]);

  // Toggle remove mode
  useEffect(() => {
    sceneManagerRef.current?.setRemoveMode(removeMode);
  }, [removeMode]);

  // Toggle shade visibility
  useEffect(() => {
    sceneManagerRef.current?.setShadeVisible(shadeVisible);
  }, [shadeVisible]);

  return <div ref={containerRef} className="canvas-area" />;
}
